/* eslint-disable no-unused-vars */
import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { Col, Row, Skeleton } from 'antd';
import { twoDecimalWithSpacesEN } from '../../helpers/formatFractional';
import { numberWithSpacesInter } from '../../helpers/formatInteger';
import dayjs from 'dayjs';
import CustomSortIcon from '../../components/CustomSortIcon/CustomSortIcon';
import { textSort } from '../../helpers/textSort';
import CustomTable from '../../components/CustomTable/CustomTable';

const TableBuySell = ({ setId, dataBuySell, isLoading, tab, currencyName }) => {
  const { t } = useTranslation('');

  const tableData = useMemo(() => {
    if (dataBuySell) {
      const data = [...dataBuySell];
      const summ = data?.reduce((acc, item) => acc + item?.value, 0);
      const oldestDate = Math.min(
        ...data.map((i) => Math.round(i.price_time / 60)),
      );
      let findFirst = false;
      return data?.map((i) => {
        let bold = false;
        if (Math.round(i.price_time / 60) === oldestDate && !findFirst) {
          bold = true;
          findFirst = true;
        }
        const element = { ...i, share: (i.value / summ) * 100, bold };
        return element;
      });
    }
    return [];
  }, [dataBuySell]);

  const totalData = useMemo(() => {
    if (dataBuySell) {
      const data = [...dataBuySell];
      return data?.reduce((acc, item) => acc + item?.value, 0);
    }
    return 0;
  }, [dataBuySell]);

  const columnsBuySell = useMemo(
    () => [
      {
        title: t('INSTRUMENT'),
        dataIndex: 'instrument',
        className: 'name',
        align: 'left',
        sorter: (a, b) => textSort(a.instrument, b.instrument),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('ISIN'),
        dataIndex: 'isin',
        align: 'left',
        sorter: (a, b) => textSort(a.isin, b.isin),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('TICKER'),
        dataIndex: 'ticker',
        align: 'right',
        sorter: (a, b) => textSort(a.ticker, b.ticker),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('NO_SHARES'),
        dataIndex: 'unitsNumber',
        align: 'right',
        render: (value, record) => {
          return value
            ? `${numberWithSpacesInter(value, null, 4)}${record.is_sell_all ? '*' : ''}`
            : '';
        },
        sorter: (a, b) => a.unitsNumber - b.unitsNumber,
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('SHARE_PRICE'),
        dataIndex: 'share_price',
        align: 'right',
        render: (value, record) => {
          return value
            ? `${numberWithSpacesInter(value, 2)} ${currencyName(record.currency_id)}`
            : '';
        },
        sorter: (a, b) => a.share_price - b.share_price,
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('VALUE'),
        dataIndex: 'value',
        className: 'value',
        align: 'right',
        render: (value) => {
          return value ? `${numberWithSpacesInter(value, 0)} kr` : '';
        },
        sorter: (a, b) => a.value - b.value,
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('PRICE_DATE'),
        dataIndex: 'price_date',
        className: 'date',
        align: 'right',
        render: (value, record) => {
          const date = !record.fx_time
            ? record.price_time
            : Math.min(record.price_time, record.fx_time);
          return `${dayjs(date * 1000).format('YYYY-MM-DD HH:mm')}`;
        },
        sorter: (a, b) =>
          Math.min(a.price_time, a.fx_time) - Math.min(b.price_time, b.fx_time),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
    ],
    [dataBuySell],
  );
  return (
    <>
      <div className="client portfolio-table transaction-table">
        <CustomTable
          className="table-buy-sell"
          rowKey={() => uuidv4()}
          rowClassName={(i) => {
            let name = 'editable-row';
            name += i.bold ? ' bold' : '';
            return name;
          }}
          columns={columnsBuySell}
          dataSource={tableData}
          loading={isLoading}
          size="middle"
          pagination={false}
        />
      </div>
      {tab === 'sell' && tableData?.length !== 0 && (
        <p className="note-bottom">* {t('SELL_ALL')}</p>
      )}
      <Row
        justify={'space-between'}
        align={'middle'}
        style={{ marginTop: '24px' }}
      >
        <Col span={18}>
          <Skeleton
            avatar={false}
            title={false}
            paragraph={{ rows: 1, width: 300 }}
            active
            loading={isLoading}
          >
            <p className="total-header">{t('TOTAL')}</p>
          </Skeleton>
        </Col>
        <Col span={6}>
          <Skeleton
            avatar={false}
            title={false}
            paragraph={{ rows: 1, width: 200 }}
            active
            loading={isLoading}
          >
            <p className="total-value">
              {`${numberWithSpacesInter(totalData, 0)} kr`}{' '}
            </p>
          </Skeleton>
        </Col>
      </Row>
    </>
  );
};

export default TableBuySell;

TableBuySell.propTypes = {
  tab: PropTypes.string,
  setId: PropTypes.number,
  dataBuySell: PropTypes.array,
  currencyName: PropTypes.func,
  isLoading: PropTypes.bool,
};
